import { useState, useEffect, useRef, useMemo } from 'react';
import { fetchSwedishBathingPlaces, fetchBathingPlacesInBounds, deduplicateBathingPlaces, fetchYrBathingTemperatures, mergeYrTemperatures } from '../services/bathingService';
import { BathingPlace, Coordinates, MapBounds } from '../types';
import { boundsOverlapRatio } from '../utils/boundsUtils';
import type { SupportedCountry } from '../utils/countryDetection';

export const useBathingPlaces = (
  active: boolean,
  zoom: number,
  currentMapCenter: Coordinates,
  currentViewBounds: MapBounds | null,
  viewportCountry: SupportedCountry | null,
) => {
  const [allPoints, setAllPoints] = useState<BathingPlace[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const swedishCacheRef = useRef<BathingPlace[] | null>(null);
  const yrTempsFetchedRef = useRef(false);
  const lastFetchedBoundsRef = useRef<MapBounds | null>(null);
  const lastCountryRef = useRef<SupportedCountry | null | undefined>(undefined);

  useEffect(() => {
    if (!active || zoom < 9 || !currentViewBounds) {
      setIsLoading(false);
      if (!active) {
        setError(false);
        setAllPoints([]);
        lastFetchedBoundsRef.current = null;
      }
      return;
    }

    // Skip if bounds haven't changed significantly and same country
    const countryChanged = lastCountryRef.current !== viewportCountry;
    if (!countryChanged && lastFetchedBoundsRef.current && boundsOverlapRatio(lastFetchedBoundsRef.current, currentViewBounds) > 0.7) {
      return;
    }

    const controller = new AbortController();

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const promises: Promise<BathingPlace[]>[] = [];

        // Norway / Denmark / other: viewport-based Overpass query
        if (viewportCountry === 'NO' || viewportCountry === 'DK' || viewportCountry === null) {
          promises.push(fetchBathingPlacesInBounds(currentViewBounds, controller.signal));
        }

        // Sweden: HaV API (full country, cached — small dataset ~500 places)
        if (viewportCountry === 'SE' || viewportCountry === null) {
          if (swedishCacheRef.current) {
            // Filter cached Swedish places to viewport
            const filtered = swedishCacheRef.current.filter(p =>
              p.location.latitude >= currentViewBounds.latMin &&
              p.location.latitude <= currentViewBounds.latMax &&
              p.location.longitude >= currentViewBounds.lonMin &&
              p.location.longitude <= currentViewBounds.lonMax
            );
            promises.push(Promise.resolve(filtered));
          } else {
            promises.push(
              fetchSwedishBathingPlaces().then(places => {
                swedishCacheRef.current = places;
                return places.filter(p =>
                  p.location.latitude >= currentViewBounds.latMin &&
                  p.location.latitude <= currentViewBounds.latMax &&
                  p.location.longitude >= currentViewBounds.lonMin &&
                  p.location.longitude <= currentViewBounds.lonMax
                );
              })
            );
          }
        }

        const results = await Promise.all(promises);
        let combined = results.flat();

        // Yr temperatures for Norwegian places (one-time, non-blocking)
        if ((viewportCountry === 'NO' || viewportCountry === null) && !yrTempsFetchedRef.current) {
          try {
            const yrTemps = await fetchYrBathingTemperatures();
            if (yrTemps.length > 0) {
              const yrNewPlaces = mergeYrTemperatures(combined, yrTemps);
              if (yrNewPlaces.length > 0) {
                combined = deduplicateBathingPlaces([...combined, ...yrNewPlaces]);
              }
              yrTempsFetchedRef.current = true;
            }
          } catch { /* ignore Yr errors */ }
        }

        // Deduplicate if multiple sources
        if (viewportCountry === null) {
          combined = deduplicateBathingPlaces(combined);
        }

        setAllPoints(combined);
        setError(false);
        lastFetchedBoundsRef.current = currentViewBounds;
        lastCountryRef.current = viewportCountry;
      } catch (e: any) {
        if (e.name !== 'AbortError') {
          console.error('Failed to fetch bathing places:', e);
          setError(true);
        }
      } finally {
        setIsLoading(false);
      }
    };

    const timer = setTimeout(fetchData, 800);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [currentViewBounds, active, zoom, viewportCountry]);

  const bathingPlaces = useMemo(() => allPoints, [allPoints]);

  return { bathingPlaces, isLoading, error };
};
